import type { PendingRequestView } from "../entitlements-api";

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

/**
 * How long a request has been waiting, in the coarsest unit that is still true.
 *
 * The queue exists to spot what has sat too long, and a figure accurate to the
 * second reads as precision nobody needs.
 *
 * `createdAt` is checked as well as `ageSeconds`, and that is the whole reason
 * it is a parameter. The service's `ageSecondsAt` CLAMPS an unparseable
 * timestamp to 0 rather than reporting it, so `ageSeconds` alone can never be
 * the sentinel: a corrupt row would arrive as 0 and render "just now", which is
 * the stale-reads-as-fresh outcome this guard exists to prevent. The server's
 * age is still what gets displayed when the timestamp is sound, since it is
 * computed against the server's clock rather than the browser's.
 */
export function formatRequestAge(ageSeconds: number, createdAt: string): string {
  if (Number.isNaN(Date.parse(createdAt))) return "unknown age";
  if (!Number.isFinite(ageSeconds) || ageSeconds < 0) return "unknown age";
  if (ageSeconds < SECONDS_PER_MINUTE) return "just now";
  if (ageSeconds < SECONDS_PER_HOUR) {
    return `${String(Math.floor(ageSeconds / SECONDS_PER_MINUTE))}m ago`;
  }
  if (ageSeconds < SECONDS_PER_DAY) {
    return `${String(Math.floor(ageSeconds / SECONDS_PER_HOUR))}h ago`;
  }
  return `${String(Math.floor(ageSeconds / SECONDS_PER_DAY))}d ago`;
}

/** Whether the server's age for this row can be believed. */
function ageIsKnown(request: PendingRequestView): boolean {
  return (
    !Number.isNaN(Date.parse(request.createdAt)) &&
    Number.isFinite(request.ageSeconds) &&
    request.ageSeconds >= 0
  );
}

/**
 * The queue with the longest-waiting request first, as a new array.
 *
 * Sorted here rather than trusted from the read, so the ordering the operator
 * works down is a property of this surface rather than of whichever index the
 * store happened to query.
 *
 * Ordered on `createdAt`, not `ageSeconds`: the service CLAMPS an unparseable
 * timestamp's age to 0, so an ageSeconds sort would file a corrupt row as the
 * newest thing in the queue. A row that cannot be placed sinks to the bottom
 * instead, where `formatRequestAge` labels it as having an unknown age. A
 * comparator alone cannot do that, because every comparison against NaN is
 * false and such a row simply stays where it started.
 */
export function sortOldestFirst(
  requests: readonly PendingRequestView[]
): readonly PendingRequestView[] {
  const placeable = requests.filter((request) => !Number.isNaN(Date.parse(request.createdAt)));
  const unplaceable = requests.filter((request) => Number.isNaN(Date.parse(request.createdAt)));
  return [
    ...[...placeable].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)),
    ...unplaceable,
  ];
}

/**
 * Whether this request has waited past the age the ENR-1937 queue-age alarm
 * fires at, so the operator sees what is about to page before it pages.
 *
 * `maxAgeSeconds` comes from the deployment (the same Pulumi value the alarm
 * thresholds on). Null means it named none, and the answer is then false for
 * every row: a flag drawn against a number invented here would stand for an
 * alarm that does not exist, and reads as the alarm being accounted for.
 *
 * A row whose age cannot be trusted is not flagged either. It is equally not
 * evidence of being INSIDE the threshold, which is why it is labelled as an
 * unknown age rather than being quietly counted as fresh.
 */
export function isAged(request: PendingRequestView, maxAgeSeconds: number | null): boolean {
  if (maxAgeSeconds === null) return false;
  if (!ageIsKnown(request)) return false;
  return request.ageSeconds > maxAgeSeconds;
}
